/**
 * ThreadHeaderLabel — the "you are here" affordance (thread-workflow UX T2).
 *
 * Renders the ACTIVE thread's title in the chat header's subtitle slot so the
 * user always knows which conversation the pane is showing — in every view
 * (floating / docked / fullscreen), on every page. Click to rename inline
 * (Enter/blur commits, Escape cancels); renaming stamps `userRenamed`, which
 * every labeler/backfill path already respects permanently.
 */
import React, { useEffect, useState } from 'react';
import { ThreadHeaderLabelButton, ThreadHeaderRenameInput } from './styles';

export interface ThreadHeaderLabelProps {
  title: string;
  onRename: (title: string) => void;
}

export const ThreadHeaderLabel: React.FC<ThreadHeaderLabelProps> = ({ title, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);

  // Track external title changes (AI label landing, cross-device rename)
  // while NOT clobbering an in-progress edit.
  useEffect(() => {
    if (!editing) setDraft(title);
  }, [title, editing]);

  const commit = () => {
    const clean = draft.trim();
    setEditing(false);
    if (clean && clean !== title) onRename(clean);
  };

  if (editing) {
    return (
      <ThreadHeaderRenameInput
        value={draft}
        autoFocus
        data-thread-header-rename=""
        aria-label="rename this thread"
        maxLength={42}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setDraft(title);
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <ThreadHeaderLabelButton
      type="button"
      data-thread-header-label=""
      title="click to rename this thread"
      aria-label={`current thread: ${title} — click to rename`}
      onClick={() => setEditing(true)}
    >
      <span data-thread-header-title="">{title}</span>
    </ThreadHeaderLabelButton>
  );
};

export default ThreadHeaderLabel;
